import React from "react";
import { X } from "lucide-react";
import { FlowpiDrawerSurface } from "../ui/flowpi-dialog";
import { SERVICE_STAGE_LABELS } from "../../lib/serviceConstants";
import { Button } from "../ui/button";
import { PassiveBadge } from "../ui/data-presentation";

const display = (value) => (
  value === null || value === undefined || value === "" ? "-" : value
);

const stageLabel = (value) => SERVICE_STAGE_LABELS[value] || display(value);

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

export function normalizeServiceStageHistory(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
}

export default function ServiceStageHistoryDialog({
  open,
  loading,
  error,
  records,
  ticket,
  onClose,
}) {
  if (!open) return null;

  return (
    <FlowpiDrawerSurface open={open} onClose={onClose} accessibleTitle="Stage History" testId="service-stage-history-dialog">
      <aside
        className="h-full w-full max-w-xl overflow-hidden border-l border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950"
      >
        <div className="flex items-start justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
              Service Ticket
            </div>
            <h2 id="service-stage-history-title" className="mt-1 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Stage History
            </h2>
            {ticket ? (
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500 dark:text-zinc-400">
                <span className="font-semibold tracking-[0.02em] text-zinc-700 dark:text-zinc-200">
                  {display(ticket.ticket_number)}
                </span>
                <span>{display(ticket.owner?.name)}</span>
                <span>{stageLabel(ticket.stage)}</span>
              </div>
            ) : null}
          </div>
          <Button
            type="button"
            onClick={onClose}
            variant="ghost"
            size="icon"
            data-action-category="close"
            aria-label="Close stage history"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </Button>
        </div>
        <div className="h-[calc(100%-93px)] overflow-y-auto p-5">
          {loading ? (
            <div className="rounded-sm border border-zinc-200 p-4 text-sm text-zinc-500 dark:border-zinc-800">
              Loading stage history...
            </div>
          ) : null}
          {error ? (
            <div className="rounded-sm border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {error}
            </div>
          ) : null}
          {!loading && !error && records.length === 0 ? (
            <div className="rounded-sm border border-zinc-200 p-4 text-sm text-zinc-500 dark:border-zinc-800">
              No stage history found.
            </div>
          ) : null}
          <div className="space-y-3">
            {records.map((record) => (
              <div key={record.id} className="rounded-sm border border-zinc-200 p-4 dark:border-zinc-800">
                <div className="flex flex-wrap items-center gap-2">
                  <PassiveBadge tone="neutral">
                    {stageLabel(record.from_stage)}
                  </PassiveBadge>
                  <span className="text-xs text-zinc-400">to</span>
                  <PassiveBadge tone="info">
                    {stageLabel(record.to_stage)}
                  </PassiveBadge>
                  <PassiveBadge tone="neutral" className="capitalize">
                    {display(record.transition_type)}
                  </PassiveBadge>
                </div>
                {record.reason ? (
                  <p className="mt-3 text-sm text-zinc-700 dark:text-zinc-300">{record.reason}</p>
                ) : null}
                <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
                  {display(record.actor_email || record.actor_id)} - {formatDateTime(record.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </FlowpiDrawerSurface>
  );
}
